/**
 * BRAND — the single source of identity for this build.
 *
 * Everything visible reads from here: the 3D palette in `theme.ts`, the HUD, the modals, the splash.
 * Change this file and the whole product changes identity; change colours anywhere else and the
 * build drifts out of its brand.
 *
 * ## This MUST be filled in
 *
 * `applied: false` means the build is still wearing the placeholder identity, and the app renders a
 * visible warning saying so (see `BrandGuard`). That is deliberate. A generated app that ships the
 * default palette looks like every other generated app — the brand language assigned to this build
 * (its colours, type, radii, voice) is the thing that makes it look designed rather than produced.
 *
 * To apply a brand: set every field below from the assigned brand language, write a real `name` and
 * `voice`, then set `applied: true`. The warning disappears when the work is actually done.
 */

export interface Brand {
	/** Has a real brand been applied? Leave false until it genuinely has — the guard depends on it. */
	applied: boolean;

	/** Product name. Shown on the splash and in the modals. */
	name: string;
	/** One line that says what this is. Shown under the name on the splash. */
	tagline: string;

	/** Core palette. Hex strings so both CSS and the 3D scene can consume them. */
	color: {
		/** Deepest ground tone — the sky's top, modal backdrops, the app background. */
		base: string;
		/** Mid tone for depth and gradients. */
		mid: string;
		/** Warm/bright horizon or highlight tone. */
		highlight: string;
		/** The single brightest mass: the player, primary buttons, key numbers. */
		primary: string;
		/** Secondary accent: collectibles, success, positive feedback. */
		accent: string;
		/** Hazard / destructive / warning. */
		danger: string;
		/** Text on top of `base`. */
		onBase: string;
	};

	/** Type. A display face carries personality; the body face carries everything else. */
	type: {
		display: string;
		body: string;
	};

	/** Corner radius in px, applied to buttons and panels. Sharp and soft read very differently. */
	radius: number;

	/** How the product speaks. Used for every string the player reads. */
	voice: string;
}

export const BRAND: Brand = {
	// PLACEHOLDER IDENTITY — replace all of this, then set applied: true.
	applied: false,

	name: 'Untitled Runner',
	tagline: 'Placeholder identity — apply the brand',

	color: {
		base: '#241a3d',
		mid: '#7b3b5c',
		highlight: '#e0894b',
		primary: '#ffe9c2',
		accent: '#5ce1c6',
		danger: '#8b5cf6',
		onBase: '#ffe9c2',
	},

	type: {
		display: '"IBM Plex Sans", system-ui, sans-serif',
		body: '"IBM Plex Sans", system-ui, sans-serif',
	},

	radius: 999,

	voice: 'Direct and quiet. Short lines, no exclamation marks, never congratulatory.',
};

/** True when this build is still wearing the placeholder identity. */
export const isPlaceholderBrand = (): boolean => !BRAND.applied;
